import React from 'react';
import { formatDate } from 'utils/date.util';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  IconButton,
  HStack,
  VStack,
  Text,
  Image,
  Badge,
  Select,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  Center,
  Link,
  Tooltip,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';

import { useColors } from './colors';

import { getMarketplaceLogo } from '../../../components/marketplaceLogo';
import { deleteListingApi } from '../../../features/listings/listings.api';

const DataTable = ({
  listings,
  selectedItems,
  setSelectedItems,
  sortBy,
  setSortBy,
  pageSize,
  setPageSize,
  currentPage,
  totalListings,
  isLoading,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    borderColor,
    headerBg,
    hoverBg,
    textColor,
    inputColor,
    filterBg,
    filterOptionHover,
    filterOptionChecked,
    filterHover,
    filterChecked,
  } = useColors();

  const selectStyles = {
    option: {
      bg: filterBg,
      color: inputColor,
    },
    '> option:hover': {
      bg: filterOptionHover + '!important',
    },
    '> option:checked': {
      bg: filterOptionChecked + '!important',
    },
  };

  const selectProps = {
    sx: selectStyles,
    _hover: {
      bg: filterHover,
    },
    _focus: {
      bg: filterChecked,
      borderColor: 'blue.300',
    },
  };

  const handleDeleteListing = (listingId) => {
    dispatch(deleteListingApi(listingId));
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Table Controls */}
      <Flex justify="space-between" align="center">
        <HStack spacing={4}>
          <Text fontSize="sm">
            {listings?.length > 0
              ? `${(currentPage - 1) * pageSize + 1} - ${Math.min(currentPage * pageSize, totalListings)} of ${totalListings}`
              : '0 - 0 of 0'}
          </Text>
          <Select
            size="sm"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            w="250px"
            {...selectProps}
          >
            <option value="dateCreated+">Date Added: New to Old</option>
            <option value="dateCreated-">Date Added: Old to New</option>
            <option value="title+">Title: A to Z</option>
            <option value="title-">Title: Z to A</option>
            <option value="price+">Price: Low to High</option>
            <option value="price-">Price: High to Low</option>
            <option value="quantity+">Quantity: Low to High</option>
            <option value="quantity-">Quantity: High to Low</option>
          </Select>
        </HStack>

        <Select
          size="sm"
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          w="150px"
          {...selectProps}
        >
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
        </Select>
      </Flex>

      {/* Main Table */}
      <Box
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflow="hidden"
      >
        {isLoading ? (
          <Center py={10}>
            <VStack spacing={4}>
              <Spinner size="lg" />
              <Text>Loading listings...</Text>
            </VStack>
          </Center>
        ) : (
          <Table variant="simple" size="sm">
            <Thead bg={headerBg}>
              <Tr>
                <Th px={4} py={3} width="40px">
                  <Checkbox
                    isChecked={selectedItems.length === listings?.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedItems(listings?.map((item) => item._id) || []);
                      } else {
                        setSelectedItems([]);
                      }
                    }}
                  />
                </Th>
                <Th width="20%">Listing Info</Th>
                <Th width="35%">Listing Details</Th>
                <Th width="22%">Listing Status</Th>
                <Th width="20%">Price & Quantity</Th>
                <Th width="40px"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {listings?.map((listing) => (
                <Tr key={listing._id} _hover={{ bg: hoverBg }} height="170px">
                  <Td px={4}>
                    <Checkbox
                      isChecked={selectedItems.includes(listing._id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems([...selectedItems, listing._id]);
                        } else {
                          setSelectedItems(
                            selectedItems.filter((id) => id !== listing._id),
                          );
                        }
                      }}
                    />
                  </Td>
                  <Td>
                    <VStack align="start" spacing={2}>
                      <Image
                        src={getMarketplaceLogo(listing?.marketplace?.name)}
                        alt={listing?.marketplace?.name}
                        height="30px"
                      />
                      <Text fontSize="sm" color={textColor}>
                        <Text as="span" fontWeight="bold">
                          Store:
                        </Text>{' '}
                        {listing.store?.name || 'N/A'}
                      </Text>
                      <Text fontSize="sm" color={textColor}>
                        <Text as="span" fontWeight="bold">
                          Supplier:
                        </Text>{' '}
                        {listing.supplier?.name || 'N/A'}
                      </Text>
                      <Text fontSize="sm" color={textColor}>
                        <Text as="span" fontWeight="bold">
                          Sup. SKU:
                        </Text>{' '}
                        {listing.supplier?.supplierIdentifier || 'N/A'}
                      </Text>
                      <Text fontSize="sm" color={textColor}>
                        <Text as="span" fontWeight="bold">
                          Partslink:
                        </Text>{' '}
                        {listing.supplier?.partslink || 'N/A'}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>
                    <HStack align="start" spacing={3}>
                      <Image
                        src={listing.details?.previewImage}
                        alt={listing.details?.title}
                        boxSize="100px"
                        objectFit="contain"
                        borderRadius="md"
                        fallback={<Box boxSize="100px" bg="gray.200" borderRadius="md" />}
                      />
                      <VStack align="start" spacing={2}>
                        <Link
                          fontSize="sm"
                          noOfLines={3}
                          href={
                            listing?.marketplace?.name === 'ebay'
                              ? `https://www.ebay.com/itm/${listing.externalPlatformId}`
                              : listing?.marketplace?.name === 'amazon'
                                ? `https://www.amazon.com/dp/${listing.externalPlatformId}`
                                : '#'
                          }
                          isExternal
                          color={textColor}
                          _hover={{ color: 'blue.500' }}
                        >
                          {listing.details?.title || 'No title'}
                        </Link>

                        <Text fontSize="sm" color={textColor}>
                          <Text as="span" fontWeight="bold">
                            Item ID / ASIN:
                          </Text>{' '}
                          {listing.externalPlatformId}
                        </Text>
                        <Text fontSize="sm" color={textColor}>
                          <Text as="span" fontWeight="bold">
                            SKU:
                          </Text>{' '}
                          {listing.sku}
                        </Text>
                      </VStack>
                    </HStack>
                  </Td>
                  <Td>
                    <VStack align="start" spacing={2}>
                      <Tooltip
                        label={listing.statusMessage || 'No status message available'}
                        placement="top"
                        hasArrow
                        bg="gray.700"
                        color="white"
                        fontSize="sm"
                        px={3}
                        py={2}
                        borderRadius="md"
                      >
                        <Badge
                          fontSize="md"
                          colorScheme={
                            listing.status === 'synced'
                              ? 'blue'
                              : listing.status === 'error'
                                ? 'red'
                                : 'yellow'
                          }
                          px={4}
                          py={1}
                          borderRadius="md"
                          cursor="help"
                        >
                          {listing.status === 'not synced'
                            ? 'Not Synced'
                            : listing.status.charAt(0).toUpperCase() +
                              listing.status.slice(1)}
                        </Badge>
                      </Tooltip>
                      <Text fontSize="sm" color={textColor}>
                        <Text as="span" fontWeight="bold">
                          Last Synced:
                        </Text>{' '}
                        {listing.lastSyncedAt
                          ? formatDate(listing.lastSyncedAt)
                          : 'Never'}
                      </Text>
                      <Text fontSize="sm" color={textColor}>
                        <Text as="span" fontWeight="bold">
                          Date Added:
                        </Text>{' '}
                        {formatDate(listing.createdAt)}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>
                    <HStack w="full" align="start">
                      {/* Left column: labels */}
                      <VStack spacing={5} align="start" mt="5px">
                        <Text fontSize="md">Price</Text>
                        <Text fontSize="md">Quantity</Text>
                      </VStack>

                      {/* Right column: boxes */}
                      <VStack spacing={2} align="end" ml="25%">
                        <Box
                          borderWidth="1px"
                          px={2}
                          py={1}
                          width="80px"
                          textAlign="center"
                          fontSize="md"
                          padding="5px"
                        >
                          <Text whiteSpace="nowrap">
                            $ {listing.details?.price || '0.00'}
                          </Text>
                        </Box>
                        <Box
                          borderWidth="1px"
                          px={2}
                          py={1}
                          width="80px"
                          textAlign="center"
                          fontSize="md"
                          padding="5px"
                        >
                          <Text whiteSpace="nowrap">
                            # {listing.details?.quantity || '0'}
                          </Text>
                        </Box>
                      </VStack>
                    </HStack>
                  </Td>
                  <Td>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<ChevronDownIcon />}
                        variant="ghost"
                        size="md"
                      />
                      <MenuList>
                        <MenuItem
                          onClick={() => navigate(`/admin/listings/${listing._id}`)}
                        >
                          Edit listing
                        </MenuItem>
                        <MenuItem onClick={() => handleDeleteListing(listing._id)}>
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* No Results */}
      {!isLoading && (!listings || listings.length === 0) && (
        <Center py={10}>
          <VStack spacing={4}>
            <Text fontSize="lg" color="gray.500">
              No listings found
            </Text>
            <Text fontSize="sm" color="gray.400">
              Try adjusting your search or filter criteria
            </Text>
          </VStack>
        </Center>
      )}
    </VStack>
  );
};

export default DataTable;
